<template>
  <admin-wrapper
    :straight-title="additionalModuleName"
    :module-enabler="moduleEnabler"
    :is-form-fetched="isFormFetched"
    :inputs="inputs"
    @save="save"
  >
    <template #fixedAssetsLayerId.input="data">
      <v-col cols="3">
        <data-input
          v-model="selectedDataSources.fixed_assets_datasource.settings['layer_id']"
          v-bind="data"
          @change="fixedAssetsLayerIdChanged"
        />
      </v-col>
    </template>
    <template #fixedAssetsLayerLengthAttribute.input="data">
      <v-col cols="3">
        <data-input
          v-bind="data"
          v-model="selectedDataSources.fixed_assets_datasource.settings['length_attribute']"
          @change="selectedDataSources.fixed_assets_datasource.changed = true"
        />
      </v-col>
    </template>
    <template #fixedAssetsLayerWaterPipesAttribute.input="data">
      <v-col cols="3">
        <data-input
          v-bind="data"
          v-model="selectedDataSources.fixed_assets_datasource.settings['water_pipes_attribute']"
          @change="selectedDataSources.fixed_assets_datasource.changed = true"
        />
      </v-col>
    </template>
    <template #fixedAssetsLayerWaterTerminalsAttribute.input="data">
      <v-col cols="3">
        <data-input
          v-bind="data"
          v-model="selectedDataSources.fixed_assets_datasource.settings['water_terminals_attribute']"
          @change="selectedDataSources.fixed_assets_datasource.changed = true"
        />
      </v-col>
    </template>
    <template #fixedAssetsLayerSewerPipesAttribute.input="data">
      <v-col cols="3">
        <data-input
          v-bind="data"
          v-model="selectedDataSources.fixed_assets_datasource.settings['sewer_pipes_attribute']"
          @change="selectedDataSources.fixed_assets_datasource.changed = true"
        />
      </v-col>
    </template>
    <template #fixedAssetsLayerSewerTerminalsAttribute.input="data">
      <v-col cols="3">
        <data-input
          v-bind="data"
          v-model="selectedDataSources.fixed_assets_datasource.settings['sewer_terminals_attribute']"
          @change="selectedDataSources.fixed_assets_datasource.changed = true"
        />
      </v-col>
    </template>
    <template #fixedAssetsWaterPipesDataSource.input="data">
      <v-col cols="3">
        <data-input
          v-bind="data"
          v-model="selectedDataSources.fixed_assets_water_pipes_datasource.datasource"
          @change="selectedDataSources.fixed_assets_water_pipes_datasource.changed = true"
        />
      </v-col>
    </template>
    <template #fixedAssetsWaterTerminalsDataSource.input="data">
      <v-col cols="3">
        <data-input
          v-bind="data"
          v-model="selectedDataSources.fixed_assets_water_terminals_datasource.datasource"
          @change="selectedDataSources.fixed_assets_water_terminals_datasource.changed = true"
        />
      </v-col>
    </template>
    <template #fixedAssetsSewerPipesDataSource.input="data">
      <v-col cols="3">
        <data-input
          v-bind="data"
          v-model="selectedDataSources.fixed_assets_sewer_pipes_datasource.datasource"
          @change="selectedDataSources.fixed_assets_sewer_pipes_datasource.changed = true"
        />
      </v-col>
    </template>
    <template #fixedAssetsSewerTerminalsDataSource.input="data">
      <v-col cols="3">
        <data-input
          v-bind="data"
          v-model="selectedDataSources.fixed_assets_sewer_terminals_datasource.datasource"
          @change="selectedDataSources.fixed_assets_sewer_terminals_datasource.changed = true"
        />
      </v-col>
    </template>
  </admin-wrapper>
</template>
<script>
import { call, get } from 'vuex-pathify';

import AdminWrapper from '@/components/AdminWrapper';
import DataInput from '@/components/DataInput';

import prepareAttributesFeatureCardMixin from '@/mixins/prepareAttributesFeatureCardMixin';

export default {
  name: 'FixedAssetsSettings',
  components: { AdminWrapper, DataInput },
  mixins: [prepareAttributesFeatureCardMixin],
  computed: {
    additionalModuleName: get('admin/additionalModules@FIXED_ASSETS_MODULE.verbose_name'),
    isModuleEnabled: get('admin/settingsValues@fixed_assets_module_enabled'),
    moduleEnabler() {
      return {
        name: 'fixed_assets_module_enabled',
        straightText: this.additionalModuleName,
        subtitle: this.$i18n.t('admin.moduleEnablerSubtitle', { suffix: this.additionalModuleName }),
      };
    },
    dataSources: get('layers/metadata'),
    waterDataSourcesMapped() {
      return this.$_objectToArray(this.dataSources)
        .filter(dataSource => dataSource.scope === 'water')
        .map(dataSource => {
          const { name, verbose_name } = dataSource;
          return { text: verbose_name, value: name };
        });
    },
    sewerDataSourcesMapped() {
      return this.$_objectToArray(this.dataSources)
        .filter(dataSource => dataSource.scope === 'sewer')
        .map(dataSource => {
          const { name, verbose_name } = dataSource;
          return { text: verbose_name, value: name };
        });
    },
    layers: get('layers/layers'),
    layersArray() {
      return this.$_objectToArray(this.layers);
    },
    layersCoreArray() {
      return this.layersArray.filter(layer => layer.type === 'features_layer' && layer.layer_scope === 'core');
    },
    layersCoreMapped() {
      return this.layersCoreArray.map(layer => {
        const { id, name } = layer;
        return { value: id, text: name };
      });
    },
    selectedFixedAssetsLayerMetadata() {
      return this.layers?.[this.selectedDataSources.fixed_assets_datasource.settings.layer_id] || undefined;
    },
    selectedFixedAssetsLayerFormSchema() {
      return this.selectedFixedAssetsLayerMetadata?.form_schema?.elements || [];
    },
    selectedFixedAssetsLayerDataSourceName() {
      return this.selectedFixedAssetsLayerMetadata?.data_source_name || '';
    },
    selectedFixedAssetsLayerDataSourceMetadata() {
      return this.dataSources?.[this.selectedFixedAssetsLayerDataSourceName] || undefined;
    },
    selectedFixedAssetsLayerDataSourceAttributesSchema() {
      return this.selectedFixedAssetsLayerDataSourceMetadata?.attributes_schema?.attributes || [];
    },
    selectedFixedAssetsLayerAttributes() {
      return this.$_flattenStructure(
        this.getForm(this.selectedFixedAssetsLayerFormSchema, this.selectedFixedAssetsLayerDataSourceAttributesSchema, {
          returnDictRelationInfo: true,
        })
      );
    },
    selectedFixedAssetsLayerNumericAttributes() {
      return this.selectedFixedAssetsLayerAttributes
        .filter(attribute => ['integer', 'decimal', 'float'].includes(attribute.dataType.name))
        .map(attribute => {
          const { attribute: value, label } = attribute;
          return { value, text: label };
        });
    },
    selectedFixedAssetsLayerTextAttributes() {
      return this.selectedFixedAssetsLayerAttributes
        .filter(attribute => attribute.dataType.name === 'text')
        .map(attribute => {
          const { attribute: value, label } = attribute;
          return { value, text: label };
        });
    },
    inputs() {
      return [
        {
          name: 'fixedAssetsLayerId',
          translationPath: 'admin.fixedAssets.fixedAssetsLayer',
          rules: [v => !!v || v == '0' || 'rules.required'],
          dataType: {
            name: 'select',
          },
          align: 'start',
          items: this.layersCoreMapped,
        },
        {
          name: 'fixedAssetsLayerLengthAttribute',
          translationPath: 'admin.fixedAssets.lengthAttribute',
          rules: [v => !!v || v == '0' || 'rules.required'],
          dataType: {
            name: 'select',
          },
          align: 'start',
          items: this.selectedFixedAssetsLayerNumericAttributes,
          disabled: !this.selectedFixedAssetsLayerDataSourceName,
        },
        {
          name: 'fixedAssetsLayerWaterPipesAttribute',
          translationPath: 'admin.fixedAssets.waterPipesAttribute',
          rules: [v => !!v || v == '0' || 'rules.required'],
          dataType: {
            name: 'select',
          },
          align: 'start',
          items: this.selectedFixedAssetsLayerTextAttributes,
          disabled: !this.selectedFixedAssetsLayerDataSourceName,
        },
        {
          name: 'fixedAssetsLayerWaterTerminalsAttribute',
          translationPath: 'admin.fixedAssets.waterTerminalsAttribute',
          rules: [v => !!v || v == '0' || 'rules.required'],
          dataType: {
            name: 'select',
          },
          align: 'start',
          items: this.selectedFixedAssetsLayerTextAttributes,
          disabled: !this.selectedFixedAssetsLayerDataSourceName,
        },
        {
          name: 'fixedAssetsLayerSewerPipesAttribute',
          translationPath: 'admin.fixedAssets.sewerPipesAttribute',
          rules: [v => !!v || v == '0' || 'rules.required'],
          dataType: {
            name: 'select',
          },
          align: 'start',
          items: this.selectedFixedAssetsLayerTextAttributes,
          disabled: !this.selectedFixedAssetsLayerDataSourceName,
        },
        {
          name: 'fixedAssetsLayerSewerTerminalsAttribute',
          translationPath: 'admin.fixedAssets.sewerTerminalsAttribute',
          rules: [v => !!v || v == '0' || 'rules.required'],
          dataType: {
            name: 'select',
          },
          align: 'start',
          items: this.selectedFixedAssetsLayerTextAttributes,
          disabled: !this.selectedFixedAssetsLayerDataSourceName,
        },
        {
          name: 'fixedAssetsWaterPipesDataSource',
          translationPath: 'admin.fixedAssets.waterPipesDataSource',
          rules: [v => !!v || v == '0' || 'rules.required'],
          dataType: {
            name: 'select',
          },
          align: 'start',
          items: this.waterDataSourcesMapped,
        },
        {
          name: 'fixedAssetsWaterTerminalsDataSource',
          translationPath: 'admin.fixedAssets.waterTerminalsDataSource',
          rules: [v => !!v || v == '0' || 'rules.required'],
          dataType: {
            name: 'select',
          },
          align: 'start',
          items: this.waterDataSourcesMapped,
        },
        {
          name: 'fixedAssetsSewerPipesDataSource',
          translationPath: 'admin.fixedAssets.sewerPipesDataSource',
          rules: [v => !!v || v == '0' || 'rules.required'],
          dataType: {
            name: 'select',
          },
          align: 'start',
          items: this.sewerDataSourcesMapped,
        },
        {
          name: 'fixedAssetsSewerTerminalsDataSource',
          translationPath: 'admin.fixedAssets.sewerTerminalsDataSource',
          rules: [v => !!v || v == '0' || 'rules.required'],
          dataType: {
            name: 'select',
          },
          align: 'start',
          items: this.sewerDataSourcesMapped,
        },
      ];
    },
  },
  data: () => ({
    isFormFetched: false,
    selectedDataSources: {
      fixed_assets_datasource: {
        changed: false,
        datasource: '',
        settings: {
          layer_id: null,
          length_attribute: '',
          water_pipes_attribute: '',
          water_terminals_attribute: '',
          sewer_pipes_attribute: '',
          sewer_terminals_attribute: '',
        },
      },
      fixed_assets_water_pipes_datasource: {
        changed: false,
        datasource: '',
        settings: {},
      },
      fixed_assets_water_terminals_datasource: {
        changed: false,
        datasource: '',
        settings: {},
      },
      fixed_assets_sewer_pipes_datasource: {
        changed: false,
        datasource: '',
        settings: {},
      },
      fixed_assets_sewer_terminals_datasource: {
        changed: false,
        datasource: '',
        settings: {},
      },
    },
  }),
  methods: {
    editDatasourceSettings: call('datasourceSettings/editDatasourceSettings'),
    getDataSources: call('layers/getLayersMetadata'),
    getDatasourceSettings: call('datasourceSettings/getDatasourceSettings'),
    getLayers: call('layers/getLayers'),
    getModulesMapping: call('admin/getModulesMapping'),
    clearFixedAssetsLayerColumns() {
      this.selectedDataSources.fixed_assets_datasource.settings = {
        layer_id: this.selectedDataSources.fixed_assets_datasource.settings.layer_id,
        length_attribute: '',
        water_pipes_attribute: '',
        water_terminals_attribute: '',
        sewer_pipes_attribute: '',
        sewer_terminals_attribute: '',
      };
      this.selectedDataSources.fixed_assets_datasource.changed = true;
    },
    fixedAssetsLayerIdChanged(value) {
      this.clearFixedAssetsLayerColumns();
      if (!value && value !== 0) return;
      this.selectedDataSources.fixed_assets_datasource.datasource = this.selectedFixedAssetsLayerDataSourceName || '';
    },
    async updateSelectedDataSource() {
      try {
        const selectedDataSourcesKeys = Object.keys(this.selectedDataSources);
        const promises = selectedDataSourcesKeys.map(selectedDataSourcesKey => {
          return this.getDatasourceSettings(selectedDataSourcesKey);
        });
        const rPromisesData = await Promise.all(promises);
        this.selectedDataSources = rPromisesData.reduce(
          (total, current, index) => ({
            ...total,
            ...{
              [selectedDataSourcesKeys[index]]: {
                datasource: current.datasource,
                settings: current.settings,
                changed: false,
              },
            },
          }),
          {}
        );
      } catch {
        //
      }
    },
    async init() {
      await Promise.all([
        this.getModulesMapping(),
        this.getDataSources({ with_attributes_schema: true }),
        this.getLayers(),
      ]);
      if (this.isModuleEnabled) {
        await this.updateSelectedDataSource();
      }
      this.isFormFetched = true;
    },
    async save() {
      try {
        const selectedDataSourcesKeys = Object.keys(this.selectedDataSources);
        const filteredSelectedDataSourcesKeys = selectedDataSourcesKeys.filter(
          key => this.selectedDataSources[key].changed
        );
        const promises = filteredSelectedDataSourcesKeys.map(selectedDataSourcesKey => {
          const settings = JSON.parse(JSON.stringify(this.selectedDataSources[selectedDataSourcesKey]));
          delete settings.changed;
          const payload = { name: selectedDataSourcesKey, settings };
          return this.editDatasourceSettings(payload);
        });
        await Promise.all(promises);
        this.selectedDataSources = selectedDataSourcesKeys.reduce(
          (total, current) => ({ ...total, [current]: { ...this.selectedDataSources[current], changed: false } }),
          {}
        );
        this.$store.set('snackbar/PUSH_SUCCESSFULLY_MESSAGE!', {
          message: this.$i18n.t('success.successEditSetting'),
        });
      } catch {
        //
      }
    },
  },
  watch: {
    isModuleEnabled(nV) {
      if (nV) {
        this.isFormFetched = false;
        this.init();
      }
    },
  },
  created() {
    if (this.isModuleEnabled) {
      this.init();
    } else {
      this.isFormFetched = true;
    }
  },
};
</script>
